import { useState, useEffect, useCallback, Fragment } from 'react'
import { useAuth } from '../contexts/AuthContext'
import {
  FiActivity,
  FiRefreshCw,
  FiLoader,
  FiAlertCircle,
  FiAlertTriangle,
  FiCheckCircle,
  FiSend,
  FiCpu,
  FiPauseCircle,
  FiChevronRight,
  FiChevronDown,
  FiDownload,
  FiZap,
} from 'react-icons/fi'

// Painel ÚNICO do Marketing (2026-07-15): visão de senior de tráfego pago com
// drill-down — clicar numa campanha expande os anúncios/funil dela; clicar num
// disparo expande o contato-a-contato. Substituiu as abas Ao Vivo/Relatórios/
// Meta Ads (decisão do Cleverson: "podia ser um painel só").
// Leads/cadastros vêm do CRM real (CTWA), não do pixel. Carrega ao abrir +
// botão Atualizar — SEM polling (limite horário do Graph da Meta, code 17).

interface LivePeriod {
  spend: number; impressions: number; clicks: number; ctr: number
  leads: number; cadastros: number
  cpc?: number; cpl?: number | null; custoCadastro?: number | null
}
interface LiveCampaign {
  id: string; name: string; effectiveStatus: string; dailyBudget: number | null
  today: LivePeriod; last7d: LivePeriod
  alerts: string[]; health: 'ok' | 'atencao' | 'critico'
}
interface LiveBlast {
  id: string; templateName: string; messageType: string; platform: string
  status: string; createdAt: string
  total: number; enviados: number; falhas: number
  entregues: number; lidos: number; cliques: number; respostas: number
  taxaEntrega: number | null; taxaLeitura: number | null
  taxaClique: number | null; taxaResposta: number | null
  pdfDoc?: string | null
}
interface MetaTemplate {
  id: string; name: string
  sent: number; delivered: number; read: number
  taxaEntrega: number | null; taxaLeitura: number | null
}
interface LiveDashboard {
  success: boolean; generatedAt: string
  totals: {
    today: { spend: number; leads: number; cadastros: number }
    last7d: { spend: number; leads: number; cadastros: number; cpl: number | null; custoCadastro: number | null }
  }
  campaigns: LiveCampaign[]
  blasts: LiveBlast[]
  metaTemplates?: MetaTemplate[]
  error?: string; hint?: string
}
interface CampaignAd {
  adId: string; adName: string; spend: number; impressions: number; clicks: number
  ctr: number; leads: number; cadastros: number; cpl: number | null; semLead: boolean
}
interface CampaignDetail {
  success: boolean; ads: CampaignAd[]
  funil: { leads: number; cadastros: number; conversao: number | null }
  error?: string
}
interface BlastContact {
  id: string; phone: string; contactName: string | null
  status?: string; latestStatus?: string | null
  sentAt?: string | null; deliveredAt?: string | null; readAt?: string | null
}

const fmtBRL = (n: number) => n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const fmtDT = (s: string) => new Date(s).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })

function AnalysisText({ text }: { text: string }) {
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean)
  const renderBold = (s: string) =>
    s.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
      part.startsWith('**') && part.endsWith('**')
        ? <strong key={i} className="text-foreground">{part.slice(2, -2)}</strong>
        : <span key={i}>{part}</span>
    )
  return (
    <ul className="space-y-1.5">
      {lines.map((l, i) => (
        <li key={i} className="flex gap-2 text-xs text-muted-foreground leading-relaxed">
          {l.startsWith('-') || l.startsWith('•') ? (
            <>
              <span className="text-violet-500 shrink-0 mt-0.5">▸</span>
              <span>{renderBold(l.replace(/^[-•]\s*/, ''))}</span>
            </>
          ) : (
            <span>{renderBold(l)}</span>
          )}
        </li>
      ))}
    </ul>
  )
}

function HealthDot({ health, alerts, status }: { health: string; alerts: string[]; status: string }) {
  if (status !== 'ACTIVE') return <FiPauseCircle className="w-3.5 h-3.5 text-muted-foreground" title="Campanha pausada" />
  if (health === 'critico') return <FiAlertCircle className="w-3.5 h-3.5 text-red-500" title={alerts.join(' · ')} />
  if (health === 'atencao') return <FiAlertTriangle className="w-3.5 h-3.5 text-amber-500" title={alerts.join(' · ')} />
  return <FiCheckCircle className="w-3.5 h-3.5 text-emerald-500" title="Saudável" />
}

function KpiCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-card neu-card border border-border rounded-lg px-3 py-2.5 min-w-0" title={label}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground truncate">{label}</div>
      <div className="text-lg font-semibold leading-tight text-foreground">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground truncate">{sub}</div>}
    </div>
  )
}

function RateBar({ pct, title }: { pct: number | null; title: string }) {
  if (pct == null) return <span className="text-[10px] text-muted-foreground">—</span>
  const color = pct >= 65 ? 'bg-emerald-500' : pct >= 45 ? 'bg-amber-500' : 'bg-red-500'
  return (
    <div className="flex items-center gap-1.5 min-w-[90px]" title={`${title}: ${pct.toFixed(1)}%`}>
      <div className="flex-1 h-1.5 rounded-full bg-secondary overflow-hidden neu-gauge">
        <div className={`h-full ${color}`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      <span className="text-[10px] tabular-nums text-muted-foreground w-9 text-right">{pct.toFixed(0)}%</span>
    </div>
  )
}

// O relatório "anúncio → CRM" (lead a lead) morava aqui até 15/07 — hoje é um
// tipo dentro do modal Exportar PDF do Acompanhamento (Recrutamento): o
// drill-down das campanhas já cobre a parte de mídia.

// Expansão da linha de campanha: anúncios + funil (fetch sob demanda).
function CampaignExpansion({ detail, loading }: { detail: CampaignDetail | null; loading: boolean }) {
  if (loading) return <div className="px-6 py-3 flex items-center gap-2 text-xs text-muted-foreground"><FiLoader className="w-3 h-3 animate-spin" /> Carregando anúncios...</div>
  if (!detail || !detail.success) return <div className="px-6 py-3 text-xs text-red-500">{detail?.error || 'Falha ao carregar detalhe da campanha'}</div>
  return (
    <div className="px-6 py-3 space-y-2 bg-secondary/20">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Anúncios (7 dias)</div>
      {detail.ads.length === 0 && <div className="text-xs text-muted-foreground">Sem tráfego nos últimos 7 dias.</div>}
      {detail.ads.map(a => (
        <div key={a.adId} className="flex items-center gap-3 text-xs">
          <span className={`w-1.5 h-1.5 rounded-full shrink-0 neu-dot ${a.semLead ? 'bg-red-500' : a.leads > 0 ? 'bg-emerald-500' : 'bg-border'}`}
            title={a.semLead ? 'Gastando sem gerar lead' : a.leads > 0 ? 'Gerando leads' : 'Sem gasto relevante'} />
          <span className="truncate max-w-[280px] text-foreground" title={a.adName}>{a.adName}</span>
          <span className="tabular-nums text-muted-foreground">{fmtBRL(a.spend)}</span>
          <span className="tabular-nums text-muted-foreground" title="CTR">{a.ctr.toFixed(2)}%</span>
          <span className="tabular-nums font-medium" title="Leads reais (CTWA)">{a.leads} lead{a.leads === 1 ? '' : 's'}</span>
          <span className="tabular-nums text-muted-foreground" title="Custo por lead">{a.cpl != null ? fmtBRL(a.cpl) : '—'}</span>
          <span className="tabular-nums" title="Cadastros completos">{a.cadastros} cad.</span>
          {a.semLead && <span className="text-[10px] text-red-500">sem lead</span>}
        </div>
      ))}
      <div className="pt-1 border-t border-border/50 flex items-center gap-3 text-xs">
        <span className="text-muted-foreground">Funil 7d:</span>
        <span className="font-medium text-foreground">{detail.funil.leads} leads → {detail.funil.cadastros} cadastros</span>
        {detail.funil.conversao != null && <span className="text-muted-foreground">({detail.funil.conversao}% de conversão)</span>}
      </div>
    </div>
  )
}

// Expansão da linha de disparo: contato a contato (fetch sob demanda).
function BlastExpansion({ contacts, loading }: { contacts: BlastContact[] | null; loading: boolean }) {
  if (loading) return <div className="px-6 py-3 flex items-center gap-2 text-xs text-muted-foreground"><FiLoader className="w-3 h-3 animate-spin" /> Carregando contatos...</div>
  if (!contacts) return <div className="px-6 py-3 text-xs text-red-500">Falha ao carregar detalhe do disparo</div>
  const shown = contacts.slice(0, 60)
  const statusOf = (c: BlastContact) => (c.latestStatus || c.status || '').toLowerCase()
  const badge = (s: string) =>
    s === 'read' ? 'text-emerald-500' : s === 'delivered' ? 'text-sky-500' : s === 'failed' ? 'text-red-500' : 'text-muted-foreground'
  const label = (s: string) =>
    s === 'read' ? 'lido' : s === 'delivered' ? 'entregue' : s === 'failed' ? 'falhou' : s === 'sent' ? 'enviado' : (s || '—')
  return (
    <div className="px-6 py-3 bg-secondary/20">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-2">Contato a contato</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1 max-h-56 overflow-y-auto pr-2">
        {shown.map(c => (
          <div key={c.id} className="flex items-center gap-2 text-xs min-w-0">
            <span className={`shrink-0 w-14 text-[10px] ${badge(statusOf(c))}`}>{label(statusOf(c))}</span>
            <span className="truncate text-foreground" title={`${c.contactName || ''} ${c.phone}`}>{c.contactName || c.phone}</span>
            {c.readAt && <span className="ml-auto shrink-0 text-[10px] text-muted-foreground" title="Lido em">{fmtDT(c.readAt)}</span>}
          </div>
        ))}
      </div>
      {contacts.length > shown.length && (
        <div className="text-[10px] text-muted-foreground mt-2">Mostrando {shown.length} de {contacts.length} contatos.</div>
      )}
    </div>
  )
}

export function MarketingLiveDashboard() {
  const { authenticatedFetch } = useAuth()

  const [data, setData] = useState<LiveDashboard | null>(null)
  const [loading, setLoading] = useState(false)
  const [analysis, setAnalysis] = useState<{ analysis: string; generatedAt: string } | null>(null)
  const [analysisLoading, setAnalysisLoading] = useState(false)

  // Drill-down: uma campanha e um disparo expandidos por vez (cache por id).
  const [openCampaign, setOpenCampaign] = useState<string | null>(null)
  const [campaignDetails, setCampaignDetails] = useState<Record<string, CampaignDetail>>({})
  const [campaignLoading, setCampaignLoading] = useState<string | null>(null)
  const [openBlast, setOpenBlast] = useState<string | null>(null)
  const [blastContacts, setBlastContacts] = useState<Record<string, BlastContact[]>>({})
  const [blastLoading, setBlastLoading] = useState<string | null>(null)

  const loadDashboard = useCallback(async () => {
    setLoading(true)
    try {
      const r = await authenticatedFetch('/api/marketing/live-dashboard')
      setData(await r.json())
    } catch (e: any) {
      setData({ success: false, error: e?.message || 'Erro de rede' } as LiveDashboard)
    } finally { setLoading(false) }
  }, [authenticatedFetch])

  const loadAnalysis = useCallback(async (force: boolean) => {
    setAnalysisLoading(true)
    try {
      const r = await authenticatedFetch(`/api/marketing/live-analysis${force ? '?refresh=1' : ''}`)
      const d = await r.json()
      // Sem refresh o backend só devolve a análise SALVA (pode ser null na 1ª
      // vez) — gerar de novo é só no botão Reanalisar.
      if (d.success && d.analysis) setAnalysis({ analysis: d.analysis, generatedAt: d.generatedAt })
    } catch { /* mantém estado anterior */ }
    finally { setAnalysisLoading(false) }
  }, [authenticatedFetch])

  const toggleCampaign = useCallback(async (id: string) => {
    if (openCampaign === id) { setOpenCampaign(null); return }
    setOpenCampaign(id)
    if (!campaignDetails[id]) {
      setCampaignLoading(id)
      try {
        const r = await authenticatedFetch(`/api/marketing/live-campaign/${id}`)
        const d = await r.json()
        setCampaignDetails(prev => ({ ...prev, [id]: d }))
      } catch (e: any) {
        setCampaignDetails(prev => ({ ...prev, [id]: { success: false, ads: [], funil: { leads: 0, cadastros: 0, conversao: null }, error: e?.message || 'Erro de rede' } }))
      } finally { setCampaignLoading(null) }
    }
  }, [openCampaign, campaignDetails, authenticatedFetch])

  const toggleBlast = useCallback(async (id: string) => {
    if (openBlast === id) { setOpenBlast(null); return }
    setOpenBlast(id)
    if (!blastContacts[id]) {
      setBlastLoading(id)
      try {
        const r = await authenticatedFetch(`/api/blast-reports/${id}/details`)
        const d = await r.json()
        setBlastContacts(prev => ({ ...prev, [id]: Array.isArray(d.details) ? d.details : Array.isArray(d) ? d : [] }))
      } catch {
        setBlastContacts(prev => ({ ...prev, [id]: [] }))
      } finally { setBlastLoading(null) }
    }
  }, [openBlast, blastContacts, authenticatedFetch])

  // Baixa o relatório contato a contato em CSV, gerado na hora a partir da fila
  // da campanha. Antes isto dependia de blast_reports.pdf_doc — campo que nenhum
  // código preenchia, então o botão vivia desabilitado. Agora vale pra qualquer
  // disparo, inclusive enquanto ele ainda está rodando.
  const [pdfLoading, setPdfLoading] = useState<string | null>(null)
  const downloadBlastReport = useCallback(async (b: LiveBlast) => {
    setPdfLoading(b.id)
    try {
      const r = await authenticatedFetch(`/api/marketing/blast/${encodeURIComponent(b.id)}/report.csv`)
      if (!r.ok) { alert('Não consegui gerar o relatório deste disparo.'); return }
      const blob = await r.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `disparo_${b.templateName || 'campanha'}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch { /* botão volta ao normal; usuário tenta de novo */ }
    finally { setPdfLoading(null) }
  }, [authenticatedFetch])

  // Carrega ao abrir; depois só no botão Atualizar (sem polling — o Graph da
  // Meta tem limite horário por usuário e o refresh de 60s estourava code 17).
  useEffect(() => {
    loadDashboard()
    loadAnalysis(false)
  }, [loadDashboard, loadAnalysis])

  if (!data) {
    return <div className="flex-1 flex items-center justify-center"><FiLoader className="w-5 h-5 animate-spin text-muted-foreground" /></div>
  }
  if (!data.success) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-2 text-center p-6">
        <FiAlertCircle className="w-6 h-6 text-red-500" />
        <div className="text-sm text-foreground">Não consegui carregar o painel</div>
        <div className="text-xs text-muted-foreground max-w-md">{data.error}{data.hint ? ` — ${data.hint}` : ''}</div>
        <button onClick={loadDashboard} className="mt-2 px-3 py-1.5 text-xs rounded-lg border border-border neu-convex hover:bg-secondary transition-colors">Tentar de novo</button>
      </div>
    )
  }

  const t = data.totals
  const ativas = data.campaigns.filter(c => c.effectiveStatus === 'ACTIVE')
  const pausadas = data.campaigns.filter(c => c.effectiveStatus !== 'ACTIVE')

  return (
    // Viewport fixa: header+KPIs com altura própria, e as 3 seções (análise,
    // campanhas, disparos) dividem o resto em faixas fixas (flex 3/5/4) —
    // tudo visível de cara, rolagem é INTERNA de cada seção (scrollbar já é
    // invisível globalmente via index.css).
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden p-4 gap-3">

      {/* Header */}
      <div className="shrink-0 flex items-center gap-2">
        <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-sm">
          <FiActivity className="w-3.5 h-3.5 text-white" />
        </div>
        <div>
          <div className="text-sm font-semibold text-foreground leading-none">Marketing — tráfego ao vivo</div>
          <div className="text-[10px] text-muted-foreground mt-0.5">
            Meta Ads × leads reais do CRM (CTWA) · atualizado {fmtDT(data.generatedAt)} · use "Atualizar" pra recarregar
          </div>
        </div>
        <button onClick={loadDashboard} disabled={loading}
          className="ml-auto p-1.5 rounded-lg hover:bg-secondary text-muted-foreground transition-colors" title="Atualizar agora">
          <FiRefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* KPIs */}
      <div className="shrink-0 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
        <KpiCard label="Gasto hoje" value={fmtBRL(t.today.spend)} />
        <KpiCard label="Leads hoje" value={String(t.today.leads)} sub={`${t.today.cadastros} cadastro${t.today.cadastros === 1 ? '' : 's'}`} />
        <KpiCard label="Gasto 7d" value={fmtBRL(t.last7d.spend)} />
        <KpiCard label="Leads 7d" value={String(t.last7d.leads)} />
        <KpiCard label="Custo por lead 7d" value={t.last7d.cpl != null ? fmtBRL(t.last7d.cpl) : '—'} />
        <KpiCard label="Cadastros 7d" value={String(t.last7d.cadastros)} />
        <KpiCard label="Custo por cadastro 7d" value={t.last7d.custoCadastro != null ? fmtBRL(t.last7d.custoCadastro) : '—'} />
      </div>

      {/* Análise do senior (IA) — faixa fixa, texto rola por dentro */}
      <div className="flex-[3] min-h-0 flex flex-col bg-card neu-card border border-border rounded-lg p-3">
        <div className="shrink-0 flex items-center gap-2 mb-2">
          <FiCpu className="w-3.5 h-3.5 text-violet-500" />
          <div className="text-xs font-semibold text-foreground">Análise do gestor de tráfego</div>
          {analysis && <div className="text-[10px] text-muted-foreground">gerada {fmtDT(analysis.generatedAt)}</div>}
          <button onClick={() => loadAnalysis(true)} disabled={analysisLoading}
            className="ml-auto flex items-center gap-1 px-2 py-1 text-[10px] rounded-lg border border-border hover:bg-secondary text-muted-foreground transition-colors"
            title="Reanalisar com os números de agora">
            {analysisLoading ? <FiLoader className="w-3 h-3 animate-spin" /> : <FiRefreshCw className="w-3 h-3" />}
            Reanalisar
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto">
          {analysis
            ? <AnalysisText text={analysis.analysis} />
            : <div className="text-xs text-muted-foreground">{analysisLoading ? 'Analisando campanhas...' : 'Sem análise ainda — clique em Reanalisar.'}</div>}
        </div>
      </div>

      {/* Campanhas (clique = drill-down) — faixa fixa, tabela rola por dentro */}
      <div className="flex-[5] min-h-0 flex flex-col bg-card neu-card border border-border rounded-lg overflow-hidden">
        <div className="shrink-0 px-3 py-2 border-b border-border flex items-center gap-2">
          <div className="text-xs font-semibold text-foreground">Campanhas Meta</div>
          <div className="text-[10px] text-muted-foreground">{ativas.length} ativa{ativas.length === 1 ? '' : 's'} · {pausadas.length} pausada{pausadas.length === 1 ? '' : 's'} · clique pra ver os anúncios</div>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 z-10 bg-card neu-thead">
              <tr className="text-[10px] uppercase tracking-wide text-muted-foreground border-b border-border">
                <th className="text-left px-3 py-1.5 font-medium">Campanha</th>
                <th className="text-right px-2 py-1.5 font-medium" title="Orçamento diário">Orç./dia</th>
                <th className="text-right px-2 py-1.5 font-medium">Gasto hoje</th>
                <th className="text-right px-2 py-1.5 font-medium">Gasto 7d</th>
                <th className="text-right px-2 py-1.5 font-medium" title="Click-through rate 7d">CTR 7d</th>
                <th className="text-right px-2 py-1.5 font-medium" title="Leads reais no WhatsApp (CTWA) 7d">Leads 7d</th>
                <th className="text-right px-2 py-1.5 font-medium" title="Custo por lead real 7d">CPL 7d</th>
                <th className="text-right px-2 py-1.5 font-medium" title="Cadastros completos vindos desta campanha 7d">Cadastros</th>
                <th className="text-right px-3 py-1.5 font-medium" title="Custo por cadastro completo 7d">R$/cad.</th>
              </tr>
            </thead>
            <tbody>
              {data.campaigns.map(c => (
                <Fragment key={c.id}>
                  <tr onClick={() => toggleCampaign(c.id)}
                    className={`border-b border-border/50 hover:bg-secondary/30 transition-colors cursor-pointer ${c.effectiveStatus !== 'ACTIVE' ? 'opacity-50' : ''}`}>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2 min-w-0 max-w-[340px]">
                        {openCampaign === c.id ? <FiChevronDown className="w-3 h-3 shrink-0 text-muted-foreground" /> : <FiChevronRight className="w-3 h-3 shrink-0 text-muted-foreground" />}
                        <HealthDot health={c.health} alerts={c.alerts} status={c.effectiveStatus} />
                        <span className="truncate text-foreground" title={c.name}>{c.name}</span>
                      </div>
                      {c.alerts.length > 0 && (
                        <div className="text-[10px] text-red-500/90 mt-0.5 truncate pl-5" title={c.alerts.join(' · ')}>{c.alerts.join(' · ')}</div>
                      )}
                    </td>
                    <td className="text-right px-2 py-2 tabular-nums text-muted-foreground">{c.dailyBudget != null ? fmtBRL(c.dailyBudget) : '—'}</td>
                    <td className="text-right px-2 py-2 tabular-nums">{fmtBRL(c.today.spend)}</td>
                    <td className="text-right px-2 py-2 tabular-nums">{fmtBRL(c.last7d.spend)}</td>
                    <td className="text-right px-2 py-2 tabular-nums">{c.last7d.ctr ? `${c.last7d.ctr.toFixed(2)}%` : '—'}</td>
                    <td className="text-right px-2 py-2 tabular-nums font-medium">{c.last7d.leads}</td>
                    <td className="text-right px-2 py-2 tabular-nums">{c.last7d.cpl != null ? fmtBRL(c.last7d.cpl) : '—'}</td>
                    <td className="text-right px-2 py-2 tabular-nums font-medium">{c.last7d.cadastros}</td>
                    <td className="text-right px-3 py-2 tabular-nums">{c.last7d.custoCadastro != null ? fmtBRL(c.last7d.custoCadastro) : '—'}</td>
                  </tr>
                  {openCampaign === c.id && (
                    <tr className="border-b border-border/50">
                      <td colSpan={9} className="p-0">
                        <CampaignExpansion detail={campaignDetails[c.id] || null} loading={campaignLoading === c.id} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
              {data.campaigns.length === 0 && (
                <tr><td colSpan={9} className="px-3 py-6 text-center text-muted-foreground">Nenhuma campanha no ad account.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Disparos (clique = contato a contato) — faixa fixa, tabela rola por dentro */}
      <div className="flex-[4] min-h-0 flex flex-col bg-card neu-card border border-border rounded-lg overflow-hidden">
        <div className="shrink-0 px-3 py-2 border-b border-border flex items-center gap-2">
          <FiSend className="w-3.5 h-3.5 text-violet-500" />
          <div className="text-xs font-semibold text-foreground">Disparos recentes (14 dias)</div>
          <div className="text-[10px] text-muted-foreground">taxa de leitura ao vivo via webhook · clique pra ver contato a contato</div>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 z-10 bg-card neu-thead">
              <tr className="text-[10px] uppercase tracking-wide text-muted-foreground border-b border-border">
                <th className="text-left px-3 py-1.5 font-medium">Template</th>
                <th className="text-left px-2 py-1.5 font-medium">Quando</th>
                <th className="text-right px-2 py-1.5 font-medium">Enviados</th>
                <th className="text-left px-2 py-1.5 font-medium" title="Entregues / enviados">Entrega</th>
                <th className="text-left px-2 py-1.5 font-medium" title="Lidos / enviados (taxa de abertura)">Leitura</th>
                <th className="text-right px-2 py-1.5 font-medium" title="Contatos que clicaram no botão do template (link rastreado por contato)">Cliques</th>
                <th className="text-right px-2 py-1.5 font-medium" title="Quem respondeu citando a mensagem do disparo">Respostas</th>
                <th className="text-right px-2 py-1.5 font-medium">Falhas</th>
                <th className="text-right px-3 py-1.5 font-medium">Ações</th>
              </tr>
            </thead>
            <tbody>
              {data.blasts.map(b => (
                <Fragment key={b.id}>
                  <tr onClick={() => toggleBlast(b.id)}
                    className="border-b border-border/50 hover:bg-secondary/30 transition-colors cursor-pointer">
                    <td className="px-3 py-2 max-w-[260px]">
                      <div className="flex items-center gap-2 min-w-0">
                        {openBlast === b.id ? <FiChevronDown className="w-3 h-3 shrink-0 text-muted-foreground" /> : <FiChevronRight className="w-3 h-3 shrink-0 text-muted-foreground" />}
                        <span className="truncate text-foreground" title={b.templateName}>{b.templateName || '(sem template)'}</span>
                      </div>
                    </td>
                    <td className="px-2 py-2 text-muted-foreground whitespace-nowrap">{fmtDT(b.createdAt)}</td>
                    <td className="text-right px-2 py-2 tabular-nums">{b.enviados}<span className="text-muted-foreground">/{b.total}</span></td>
                    <td className="px-2 py-2"><RateBar pct={b.taxaEntrega} title="Entrega" /></td>
                    <td className="px-2 py-2"><RateBar pct={b.taxaLeitura} title="Leitura" /></td>
                    <td className={`text-right px-2 py-2 tabular-nums ${b.cliques > 0 ? 'text-violet-500 font-medium' : 'text-muted-foreground'}`}>{b.cliques}{b.taxaClique != null && b.cliques > 0 && <span className="text-muted-foreground font-normal"> ({b.taxaClique.toFixed(0)}%)</span>}</td>
                    <td className="text-right px-2 py-2 tabular-nums">{b.respostas}{b.taxaResposta != null && <span className="text-muted-foreground"> ({b.taxaResposta.toFixed(0)}%)</span>}</td>
                    <td className={`text-right px-2 py-2 tabular-nums ${b.falhas > 0 ? 'text-red-500' : 'text-muted-foreground'}`}>{b.falhas}</td>
                    <td className="text-right px-3 py-2">
                      <button
                        onClick={e => { e.stopPropagation(); downloadBlastReport(b) }}
                        disabled={pdfLoading === b.id}
                        className="p-1 rounded hover:bg-secondary text-muted-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        title="Baixar relatório contato a contato (CSV)">
                        {pdfLoading === b.id ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiDownload className="w-3.5 h-3.5" />}
                      </button>
                    </td>
                  </tr>
                  {openBlast === b.id && (
                    <tr className="border-b border-border/50">
                      <td colSpan={9} className="p-0">
                        <BlastExpansion contacts={blastContacts[b.id] ?? null} loading={blastLoading === b.id} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
              {data.blasts.length === 0 && (
                <tr><td colSpan={9} className="px-3 py-6 text-center text-muted-foreground">Nenhum disparo nos últimos 14 dias.</td></tr>
              )}
            </tbody>
          </table>

          {/* Templates Meta (automático) — métricas coletadas pela própria Meta
              (template_analytics do WABA): pega disparos feitos FORA do CRM,
              direto no WhatsApp Manager. Insights habilitados em 15/07/2026,
              sem retroativo — o histórico acumula daqui pra frente. */}
          <div className="px-3 py-2 border-t border-border">
            <div className="flex items-center gap-2 mb-1.5">
              <FiZap className="w-3 h-3 text-violet-500" />
              <div className="text-[10px] uppercase tracking-wide font-semibold text-foreground">Templates de marketing — dados da Meta (14 dias)</div>
              <div className="text-[10px] text-muted-foreground" title="Coleta automática da Meta desde 15/07/2026 — inclui disparos feitos fora do CRM (WhatsApp Manager)">automático · pega disparos feitos fora do CRM</div>
            </div>
            {(data.metaTemplates && data.metaTemplates.length > 0) ? (
              <table className="w-full text-xs">
                <tbody>
                  {data.metaTemplates.map(t => (
                    <tr key={t.id} className="border-b border-border/30 last:border-0">
                      <td className="py-1.5 pr-2 max-w-[260px]"><span className="truncate block text-foreground" title={t.name}>{t.name}</span></td>
                      <td className="text-right px-2 py-1.5 tabular-nums">{t.sent}<span className="text-muted-foreground"> enviados</span></td>
                      <td className="px-2 py-1.5"><RateBar pct={t.taxaEntrega} title="Entrega" /></td>
                      <td className="px-2 py-1.5"><RateBar pct={t.taxaLeitura} title="Leitura" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-[10px] text-muted-foreground">Sem envios de template de marketing registrados pela Meta no período. A coleta foi ativada em 15/07/2026 — disparos a partir de agora aparecem aqui sozinhos, mesmo os feitos fora do CRM.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
